import React from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Button } from '@cp/ds/src/components/button';
import { Typography } from '@cp/ds/src/components/typography';
import { Image } from '@cp/ds/src/components/image';
import { toAsset } from '@cp/shared/image';
import { styled } from '@cp/ui';
import { ClientWebPageProps } from '@common/model/meta/clientWebPageProps';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useIsMobile } from '@cp/ds/src/hooks';
import { ClientType } from '@common/model/client/clientType';
import { billingTariffApi, getTariffPerMonthPrice } from '@cp/entities/tariff';
import { useCurrentCity } from '@cp/entities/location';
import { useActivatedPromocodesFreePeriod } from '@cp/entities/promocode';
import { LoginButton } from 'src/features/auth';
import { CatalogSpecializationTagCloud } from 'src/widgets/CatalogSpecializationTagCloudNew';
import VectorVertical from 'src/assets/shapes/vectors/vectorVertical.svg';
import nannies from 'src/assets/images/nannies.png';
import nurses from 'src/assets/images/nurses.png';
import tutors from 'src/assets/images/tutors.png';
import housekeepers from 'src/assets/images/housekeepers.png';
import nanniesMob from 'src/assets/images/nanniesMob.png';
import nursesMob from 'src/assets/images/nursesMob.png';
import tutorsMob from 'src/assets/images/tutorsMob.png';
import housekeepersMob from 'src/assets/images/housekeepersMob.png';
import { useStartBillingSubscription } from 'src/features/billing';
import { Price } from 'src/shared/ui/price';
import { useClientProfile } from 'src/entities/client';

interface PrimaryHeaderBannerProps {
  hasOffset?: boolean;
  loggedIn: boolean;
  header?: React.ReactNode;
  content?: React.ReactNode;
  text?: boolean;
  subtitle?: string | false;
  subtitleLight?: string | false;
  pageProps: ClientWebPageProps[ClientWebPage.Catalog];
}
const headerBannerImagesWithWebp = (isMobile: boolean) =>
  ({
    1: isMobile ? toAsset(nanniesMob) : toAsset(nannies),
    2: isMobile ? toAsset(nursesMob) : toAsset(nurses),
    3: isMobile ? toAsset(tutorsMob) : toAsset(tutors),
    4: isMobile ? toAsset(housekeepersMob) : toAsset(housekeepers),
  }) as const;

const Root = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '20px',
  paddingBottom: 0,
  paddingInline: '12px',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    paddingBottom: '20px',
    borderRadius: '16px',
    paddingInline: '24px',
    backgroundColor: 'white',
  },
}));

const StyledImage = styled(Image)(({ theme }) => ({
  position: 'absolute',
  overflow: 'hidden',
  height: '100%',
  maxHeight: '300px',
  top: 0,
  right: 0,
  zIndex: 0,
  [theme.breakpoints.up('md')]: {
    opacity: 0.5,
  },
}));

const StyledText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
  },
}));

export const PrimaryHeaderBannerV2: React.FC<PrimaryHeaderBannerProps> = ({
  header,
  content,
  pageProps,
  subtitle,
  loggedIn,
  text,
  subtitleLight,
}) => {
  const isMobile = useIsMobile();
  const freePeriod = useActivatedPromocodesFreePeriod();
  const [start] = useStartBillingSubscription();
  const clientProfile = useClientProfile();
  const bannerImages = headerBannerImagesWithWebp(isMobile);
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const imageAsset = bannerImages[pageProps.careerId];
  const { data: tariffs } = billingTariffApi.endpoints.getBillingTariffs.useQuery({
    clientType: clientProfile?.type || ClientType.Employer,
    cityId: clientProfile?.cityId || currentCity?.id,
  });
  const tariff = (tariffs || []).find((t) => t.period === 'P3M');
  const DEFAULT_MONTHLY_PRICE = 249;
  const monthlyPrice = React.useMemo(() => (tariff ? getTariffPerMonthPrice(tariff) : DEFAULT_MONTHLY_PRICE), [tariff]);

  return (
    <Root>
      <StyledImage alt="обученный персонал" asset={toAsset(imageAsset)} disableLazyLoad={true} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>{header}</Box>

      <Box sx={{ position: 'relative', zIndex: 1, mb: 0.5 }}>
        {content}

        {subtitle && (
          <Typography component="div" variant="body bold">
            {subtitle}
          </Typography>
        )}

        {subtitleLight && (
          <StyledText component="div" sx={{ color: 'gray' }} variant="body">
            {subtitleLight}
          </StyledText>
        )}

        {text && loggedIn && (
          <>
            <StyledText component="div" variant="body">
              Подписка — полный доступ к контактам.
            </StyledText>
            <Stack direction="row" gap={'3px'} sx={{ flexWrap: 'wrap' }}>
              <StyledText component="div" variant="body bold">
                {freePeriod ? 'Попробуйте ' + freePeriod + ' бесплатно' : 'Оформить подписку'}
              </StyledText>
              <StyledText component="div" variant="body">
                Далее от <Price value={monthlyPrice} /> за месяц
              </StyledText>
            </Stack>
          </>
        )}

        {loggedIn ? (
          <Button color="gray" onClick={start} size="small" sx={{ mt: { xs: 2.5, md: 3 } }} variant="primary">
            {freePeriod ? 'Попробовать' : 'Оформить подписку'}
          </Button>
        ) : (
          <LoginButton color="gray" sx={{ my: { xs: 2.5, md: 3 } }} variant="primary">
            Войти или зарегистрироваться
          </LoginButton>
        )}

        {!loggedIn && <CatalogSpecializationTagCloud careerId={pageProps.careerId} itemType={pageProps.catalogType} />}
      </Box>
      <Stack
        sx={{
          display: { xs: 'none', md: 'flex' },
          color: 'gray background',
          position: 'absolute',
          top: 0,
          right: 0,
          transform: 'scale(-1)',
          width: '70px',
        }}
      >
        <VectorVertical />
      </Stack>
    </Root>
  );
};
